import { VENMO_AUTOMATION_STAGE_STATUSES } from './venmoAutomationStageStatuses'
import * as venmoUrls from './venmoUrls'
import { VenmoSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import { VenmoRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import { getAttrPromise, getDomPromise, wait } from '../interruptiblePromises'
import {
  evaluateXpath,
  getCommonAncestor,
  getNearestElementMatch,
} from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement } from '../utils'

// Settings property functions
const TARGET_VALUES = {
  [VenmoSettingKey.PAYMENT_PRIVACY_FUTURE_PAYMENTS]: 'Private',
}

const cantUndoFns = {
  [VenmoSettingKey.PAYMENT_PRIVACY_FUTURE_PAYMENTS]: defaultCantUndoFn,
  [VenmoSettingKey.PAYMENT_PRIVACY_PAST_PAYMENTS]: () => true,
}

const getCurrentValueFn = (key) => {
  switch (key) {
    case VenmoSettingKey.PAYMENT_PRIVACY_FUTURE_PAYMENTS:
      return getCurrentValueFnFromKey(key, (value) => value)
    case VenmoSettingKey.PAYMENT_PRIVACY_PAST_PAYMENTS:
      return null
    default:
      return null
  }
}

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => value === TARGET_VALUES[key])

const getCantUndoFn = (key) => cantUndoFns[key]

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// DOM helpers
async function getFuturePaymentsSectionDiv(): Promise<Node> {
  const titleNode = await getElement<HTMLElement>(
    document,
    '//*[text() = "Future payments"]',
  )

  let sectionDiv
  if (titleNode) {
    const radioButtonNode = getNearestElementMatch(
      titleNode,
      '*[@role="radio"]',
      'following',
    )

    if (radioButtonNode) {
      sectionDiv = getCommonAncestor(titleNode, radioButtonNode)
    }
  }

  if (!sectionDiv) {
    throw new Error("Couldn't find future payments section")
  }

  return sectionDiv
}

function getPastPaymentsSectionDiv(): HTMLElement {
  const titleNode = evaluateXpath<HTMLElement>(
    document,
    '//*[text() = "Past payments"]',
  )[0]

  let sectionDiv
  if (titleNode) {
    const buttonNode = getNearestElementMatch(
      titleNode,
      '*[@type="button" or text() = "Change to Private"]',
      'following',
    )

    if (buttonNode) {
      sectionDiv = getCommonAncestor<HTMLElement>(titleNode, buttonNode)
    }
  }

  if (!sectionDiv) {
    throw new Error("Couldn't find past payments section")
  }

  return sectionDiv
}

function getSelectedFuturePaymentsSetting(sectionDiv: Node): HTMLElement {
  const settingNode = evaluateXpath<HTMLElement>(
    sectionDiv,
    `
      descendant::*[@role = "radio" and @aria-checked="true"]
      /descendant::*[text() = "Public" or text() = "Friends" or text() = "Private"]
    `,
  )[0]

  if (!settingNode) {
    throw new Error("Couldn't find selected future payments setting")
  }

  return settingNode
}

function getFuturePaymentsSettingForPrivacyLevel(
  sectionDiv: Node,
  privacyLevel: string,
): HTMLElement {
  const settingNode = evaluateXpath<HTMLElement>(
    sectionDiv,
    `
      descendant::*[@role = "radio" and count(./descendant::*[text() = "${privacyLevel}"]) > 0]
    `,
  )[0]

  if (!settingNode) {
    throw new Error("Couldn't find future payments setting")
  }

  return settingNode
}

function getPastPaymentsSettingForPrivacyLevel(
  sectionDiv: Node,
  privacyLevel: string,
): HTMLElement {
  const settingNode = evaluateXpath<HTMLElement>(
    sectionDiv,
    `
      descendant::*[
        @type = "button" and
        count(./descendant-or-self::*[text() = "Change to ${privacyLevel}"]) > 0
      ]
    `,
  )[0]

  if (!settingNode) {
    throw new Error("Couldn't find past payments setting")
  }

  return settingNode
}

function getPastPaymentsModalConfirmationButton(
  modalNode: Node,
  optionText: string,
): HTMLElement {
  const confirmationButton = evaluateXpath<HTMLElement>(
    modalNode,
    `
      descendant::*[
        @type = "button" and
        count(./descendant-or-self::*[text() = "${optionText}"]) > 0
      ]
    `,
  )[0]

  if (!confirmationButton) {
    throw new Error("Couldn't find past payments modal confirmation button")
  }

  return confirmationButton
}

async function selectFuturePaymentsPrivacySetting(
  settingNode: HTMLElement,
  isDry: boolean,
): Promise<MutationRecord | null> {
  if (isDry || settingNode.ariaChecked === 'true') {
    return null
  }

  const listener = getAttrPromise(settingNode, 'aria-checked')
  settingNode.click()
  return listener
}

async function selectPastPaymentsPrivacySetting(
  settingsNode: HTMLElement,
  isDry: boolean,
): Promise<Node | void> {
  const openModalListener = getDomPromise(
    document,
    (node) =>
      evaluateXpath(node, 'descendant-or-self::*[@role="dialog"]').length > 0,
  )

  settingsNode.click()
  const confirmationModal = await openModalListener

  const confirmationButton = getPastPaymentsModalConfirmationButton(
    confirmationModal,
    isDry ? 'No' : 'Yes',
  )
  const closeModalListener = getDomPromise(
    document,
    (node) =>
      evaluateXpath(node, 'descendant-or-self::*[@role="dialog"]').length > 0,
  )
  confirmationButton.click()

  return closeModalListener
}

// Setup gather stage definitions

enum StageKey {
  GATHER_SETTING_FUTURE_PAYMENTS_PRIVACY = 'GATHER_SETTING_FUTURE_PAYMENTS_PRIVACY',
  UPDATE_SETTING_FUTURE_PAYMENTS_PRIVACY = 'UPDATE_SETTING_FUTURE_PAYMENTS_PRIVACY',
  UPDATE_SETTING_PAST_PAYMENTS_PRIVACY = 'UPDATE_SETTING_PAST_PAYMENTS_PRIVACY',
  UNDO_SETTING_FUTURE_PAYMENTS_PRIVACY = 'UNDO_SETTING_FUTURE_PAYMENTS_PRIVACY',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_FUTURE_PAYMENTS_PRIVACY,
    status: VENMO_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: venmoUrls.privacyUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      const sectionDiv = await getFuturePaymentsSectionDiv()
      const selectedSetting = getSelectedFuturePaymentsSetting(sectionDiv)

      await updateStoredInitialValues(
        {
          [VenmoSettingKey.PAYMENT_PRIVACY_FUTURE_PAYMENTS]:
            selectedSetting.textContent,
        },
        VenmoRecommendationKey.VENMO_PAYMENT_PRIVACY,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_FUTURE_PAYMENTS_PRIVACY,
    status: VENMO_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: venmoUrls.privacyUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      // Skip if not selected
      if (
        !settingsSelected?.[VenmoSettingKey.PAYMENT_PRIVACY_FUTURE_PAYMENTS]
      ) {
        return null
      }

      const sectionDiv = await getFuturePaymentsSectionDiv()
      const targetSetting = getFuturePaymentsSettingForPrivacyLevel(
        sectionDiv,
        'Private',
      )

      await selectFuturePaymentsPrivacySetting(targetSetting, isDry)
      await wait(1000)

      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_PAST_PAYMENTS_PRIVACY,
    status: VENMO_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: venmoUrls.privacyUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      if (!settingsSelected?.[VenmoSettingKey.PAYMENT_PRIVACY_PAST_PAYMENTS]) {
        return null
      }

      const sectionDiv = await getPastPaymentsSectionDiv()
      const settingNode = getPastPaymentsSettingForPrivacyLevel(
        sectionDiv,
        'Private',
      )
      return selectPastPaymentsPrivacySetting(settingNode, isDry)
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_FUTURE_PAYMENTS_PRIVACY,
    status: VENMO_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: venmoUrls.privacyUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry, settingsSelected } = userContext

      // Skip if not selected
      if (
        !settingsSelected?.[VenmoSettingKey.PAYMENT_PRIVACY_FUTURE_PAYMENTS]
      ) {
        return null
      }

      const sectionDiv = await getFuturePaymentsSectionDiv()
      const targetSetting = getFuturePaymentsSettingForPrivacyLevel(
        sectionDiv,
        initialValues[VenmoSettingKey.PAYMENT_PRIVACY_FUTURE_PAYMENTS],
      )

      await selectFuturePaymentsPrivacySetting(targetSetting, isDry)
      await wait(1000)

      return null
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
